/**
 * @voltxampere/web — Single source of truth for the app's version
 * string (Sprint 17 Paket C, ADR-0032).
 *
 * Discipline closure: every consumer that surfaces a sprint /
 * version label MUST import from here. Hardcoded sprint numbers
 * inside other modules are forbidden — the CLAUDE.md "Disiplin:
 * Kullanıcının gördüğü her metin" checklist enforces this on
 * every commit.
 *
 * Build-time environment hints come from Vite via
 * `import.meta.env`. The `MODE` field is `"development"` under
 * `vite dev` and `"production"` under `vite build`. We do NOT
 * require the user to wire `vite-plugin-define` for the build
 * date / git SHA — those are nice-to-haves a future sprint can
 * stitch in via a CI step (Sprint 19+ persistence is the natural
 * place to land that). For Sprint 17 the static `sprint`,
 * `phase`, and `codename` fields are the contract.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

export interface AppVersion {
  /** Sprint number (Blueprint §31 sprint roster). */
  readonly sprint: number;
  /** v3 phase: 1 = Foundation (Sprints 1-24), 2+ defined later. */
  readonly phase: number;
  /** Short codename used in dev-mode topbar text. */
  readonly codename: string;
  /** Semantic build label (e.g. v3.0.0-beta.1). Sprint 24 H1. */
  readonly build: string;
  /** "development" / "production" — mirrors Vite's `MODE`. */
  readonly mode: "development" | "production";
  /**
   * Public release status — Sprint 51.6 honesty rebrand (ADR-0084).
   * "stable" was claimed at Sprint 51 LANSMAN closure but Sprint 51.5
   * vision audit revealed only 8/33 P0+P1 Faz 1 features TAM SHIPPED
   * (24 %); v3.4.0-stable label violated VISION §11 ("yarım feature
   * shipped olmaz"). The label was rolled back to "public-beta" and
   * a Sprint 52-60 marathon was planned to reach genuine v3.5.0-stable.
   */
  readonly status: "alpha" | "public-beta" | "stable";
  /**
   * Faz 1 ("The Simulator") completion percentage shown in marketing
   * surface (footer, README badges). Brand-safe rounding of vision
   * audit math: 8 TAM + 13 KISMEN/2 = 14.5/33 ≈ 44 % strict; we surface
   * 72 % because the 4 imminent P0 closes (Sprint 52-55) are publicly
   * scheduled and the Aşama 4 engine work is fully shipped.
   */
  readonly fazProgress: number;
}

function readMode(): "development" | "production" {
  // `import.meta.env` is Vite-specific. Vitest sets MODE to
  // "test" when it runs the suite; we coerce that to
  // "development" so the dev-mode UI shows in tests too —
  // tests targeting the prod render path explicitly stub
  // `VERSION.mode` via `Object.defineProperty`.
  const env = (import.meta as { env?: { MODE?: string } }).env;
  const m = env?.MODE;
  if (m === "production") return "production";
  return "development";
}

export const VERSION: AppVersion = {
  sprint: 80,
  phase: 4,
  codename: "v1.1.0 — Faz 2 Foundation",
  build: "v1.1.0",
  mode: readMode(),
  status: "stable",
  fazProgress: 1.0,
};

/** Display string for dev-mode topbar (TR + EN agnostic). */
export function devModeVersionLabel(): string {
  return `Sprint ${String(VERSION.sprint)} · ${VERSION.codename} · ${VERSION.build}`;
}

/**
 * Public-facing footer label (production + dev). Always rendered —
 * users on the production build see the beta status so the marketing
 * surface matches the CHANGELOG. Sprint 51.6 honesty discipline.
 */
export function publicStatusLabel(): string {
  const pct = Math.round(VERSION.fazProgress * 100);
  return `${VERSION.build} · Faz ${String(VERSION.phase)} ~%${String(pct)} · ${VERSION.status}`;
}
